import type { CategoryId } from '../game/types'

export type Lang = 'en' | 'tr' | 'es' | 'ru' | 'fr'

export const LANGS: { id: Lang; label: string }[] = [
  { id: 'en', label: 'English' },
  { id: 'tr', label: 'Türkçe' },
  { id: 'es', label: 'Español' },
  { id: 'ru', label: 'Русский' },
  { id: 'fr', label: 'Français' },
]

export interface Strings {
  daily: string
  tagline: string
  lede: (n: number) => string
  categories: Record<CategoryId, string>
  tagSolved: string
  tagPlayed: string
  tagResume: string
  homeFoot: string
  back: string
  hintPlay: string
  hintDone: string
  earlier: string
  later: string
  checksLeft: (left: number, max: number) => string
  checkOrder: string
  checkAgain: string
  resultWon: (used: number, max: number) => string
  resultLost: (best: number, total: number) => string
  shareResult: string
  copied: string
  nextPuzzle: string
  themeToLight: string
  themeToDark: string
  langLabel: string
  disclaimer: string
  inspiredBy: string
  play: string
  endless: string
  nextRound: string
  shareImage: string
  statStreak: string
  statBest: string
  statPlayed: string
  statWin: string
  solvedLabel: string
}

export const STRINGS: Record<Lang, Strings> = {
  en: {
    daily: 'Daily',
    tagline: 'Put history in order.',
    lede: (n) =>
      `Sort ${n} sports moments by when they happened. A fresh lineup for every category, every day.`,
    categories: { football: 'Football', nba: 'NBA', tennis: 'Tennis', f1: 'Formula 1', mixed: 'Mixed' },
    tagSolved: 'Solved',
    tagPlayed: 'Played',
    tagResume: 'Resume',
    homeFoot: 'Drag to reorder · earliest at the top · three checks',
    back: 'Index',
    hintPlay: 'Drag into order — earliest at the top.',
    hintDone: 'The full timeline, earliest to latest.',
    earlier: 'Earlier',
    later: 'Later',
    checksLeft: (l, m) => `${l} of ${m} checks left`,
    checkOrder: 'Check the order',
    checkAgain: 'Check again',
    resultWon: (u, m) => `Solved in ${u}/${m}.`,
    resultLost: (b, t) => `Out of checks — best ${b}/${t}.`,
    shareResult: 'Share result',
    copied: 'Copied to clipboard',
    nextPuzzle: 'Next puzzle in',
    themeToLight: 'Switch to light theme',
    themeToDark: 'Switch to dark theme',
    langLabel: 'Language',
    disclaimer: 'An independent daily puzzle · dates from public records',
    inspiredBy: 'Inspired by hisorty.app',
    play: 'Play',
    endless: 'Endless',
    nextRound: 'Next puzzle',
    shareImage: 'Share image',
    statStreak: 'Streak',
    statBest: 'Best',
    statPlayed: 'Played',
    statWin: 'Win %',
    solvedLabel: 'Solved',
  },
  tr: {
    daily: 'Günlük',
    tagline: 'Tarihi sıraya koy.',
    lede: (n) =>
      `${n} spor anını gerçekleşme sırasına göre diz. Her kategori için her gün yeni bir dizilim.`,
    categories: { football: 'Futbol', nba: 'NBA', tennis: 'Tenis', f1: 'Formula 1', mixed: 'Karışık' },
    tagSolved: 'Çözüldü',
    tagPlayed: 'Oynandı',
    tagResume: 'Devam et',
    homeFoot: 'Sürükleyerek sırala · en eski en üstte · üç deneme',
    back: 'Liste',
    hintPlay: 'Sürükleyerek sırala — en eski en üstte.',
    hintDone: 'Tüm zaman çizelgesi, eskiden yeniye.',
    earlier: 'Önce',
    later: 'Sonra',
    checksLeft: (l, m) => `${m} denemeden ${l} kaldı`,
    checkOrder: 'Sıralamayı kontrol et',
    checkAgain: 'Tekrar kontrol et',
    resultWon: (u, m) => `${u}/${m} denemede çözüldü.`,
    resultLost: (b, t) => `Deneme bitti — en iyi ${b}/${t}.`,
    shareResult: 'Sonucu paylaş',
    copied: 'Panoya kopyalandı',
    nextPuzzle: 'Sonraki bulmaca',
    themeToLight: 'Açık temaya geç',
    themeToDark: 'Koyu temaya geç',
    langLabel: 'Dil',
    disclaimer: 'Bağımsız bir günlük bulmaca · tarihler kamuya açık kaynaklardan',
    inspiredBy: "hisorty.app'ten ilhamla",
    play: 'Oyna',
    endless: 'Sonsuz',
    nextRound: 'Sonraki',
    shareImage: 'Görseli paylaş',
    statStreak: 'Seri',
    statBest: 'En iyi',
    statPlayed: 'Oynanan',
    statWin: 'Kazanma',
    solvedLabel: 'Çözülen',
  },
  es: {
    daily: 'Diario',
    tagline: 'Pon la historia en orden.',
    lede: (n) =>
      `Ordena ${n} momentos del deporte según cuándo ocurrieron. Una nueva alineación para cada categoría, cada día.`,
    categories: { football: 'Fútbol', nba: 'NBA', tennis: 'Tenis', f1: 'Fórmula 1', mixed: 'Mixto' },
    tagSolved: 'Resuelto',
    tagPlayed: 'Jugado',
    tagResume: 'Continuar',
    homeFoot: 'Arrastra para ordenar · el más antiguo arriba · tres intentos',
    back: 'Índice',
    hintPlay: 'Arrastra para ordenar — el más antiguo arriba.',
    hintDone: 'La línea de tiempo completa, del más antiguo al más reciente.',
    earlier: 'Antes',
    later: 'Después',
    checksLeft: (l, m) => `${l} de ${m} intentos restantes`,
    checkOrder: 'Comprobar orden',
    checkAgain: 'Comprobar de nuevo',
    resultWon: (u, m) => `Resuelto en ${u}/${m}.`,
    resultLost: (b, t) => `Sin intentos — mejor ${b}/${t}.`,
    shareResult: 'Compartir resultado',
    copied: 'Copiado al portapapeles',
    nextPuzzle: 'Próximo reto en',
    themeToLight: 'Cambiar a tema claro',
    themeToDark: 'Cambiar a tema oscuro',
    langLabel: 'Idioma',
    disclaimer: 'Un puzzle diario independiente · fechas de registros públicos',
    inspiredBy: 'Inspirado en hisorty.app',
    play: 'Jugar',
    endless: 'Sin fin',
    nextRound: 'Siguiente',
    shareImage: 'Compartir imagen',
    statStreak: 'Racha',
    statBest: 'Mejor',
    statPlayed: 'Jugadas',
    statWin: 'Victorias %',
    solvedLabel: 'Resueltos',
  },
  ru: {
    daily: 'Ежедневный',
    tagline: 'Расставь историю по порядку.',
    lede: (n) =>
      `Расставьте ${n} спортивных моментов по времени. Новая подборка в каждой категории каждый день.`,
    categories: { football: 'Футбол', nba: 'НБА', tennis: 'Теннис', f1: 'Формула 1', mixed: 'Микс' },
    tagSolved: 'Решено',
    tagPlayed: 'Сыграно',
    tagResume: 'Продолжить',
    homeFoot: 'Перетаскивайте · самое раннее сверху · три проверки',
    back: 'Список',
    hintPlay: 'Перетащите по порядку — самое раннее сверху.',
    hintDone: 'Полная хронология, от раннего к позднему.',
    earlier: 'Раньше',
    later: 'Позже',
    checksLeft: (l, m) => `Осталось проверок: ${l} из ${m}`,
    checkOrder: 'Проверить порядок',
    checkAgain: 'Проверить снова',
    resultWon: (u, m) => `Решено за ${u}/${m}.`,
    resultLost: (b, t) => `Проверки закончились — лучшее ${b}/${t}.`,
    shareResult: 'Поделиться',
    copied: 'Скопировано',
    nextPuzzle: 'Следующая загадка через',
    themeToLight: 'Светлая тема',
    themeToDark: 'Тёмная тема',
    langLabel: 'Язык',
    disclaimer: 'Независимая ежедневная головоломка · даты из открытых источников',
    inspiredBy: 'Вдохновлено hisorty.app',
    play: 'Играть',
    endless: 'Бесконечный',
    nextRound: 'Следующая',
    shareImage: 'Поделиться картинкой',
    statStreak: 'Серия',
    statBest: 'Рекорд',
    statPlayed: 'Сыграно',
    statWin: 'Побед %',
    solvedLabel: 'Решено',
  },
  fr: {
    daily: 'Quotidien',
    tagline: "Remets l'histoire dans l'ordre.",
    lede: (n) =>
      `Classe ${n} moments du sport selon leur date. Une nouvelle grille pour chaque catégorie, chaque jour.`,
    categories: { football: 'Football', nba: 'NBA', tennis: 'Tennis', f1: 'Formule 1', mixed: 'Mixte' },
    tagSolved: 'Résolu',
    tagPlayed: 'Joué',
    tagResume: 'Reprendre',
    homeFoot: 'Glisse pour classer · le plus ancien en haut · trois essais',
    back: 'Index',
    hintPlay: 'Glisse pour classer — le plus ancien en haut.',
    hintDone: 'La chronologie complète, du plus ancien au plus récent.',
    earlier: 'Avant',
    later: 'Après',
    checksLeft: (l, m) => `${l}/${m} essais restants`,
    checkOrder: "Vérifier l'ordre",
    checkAgain: 'Vérifier à nouveau',
    resultWon: (u, m) => `Résolu en ${u}/${m}.`,
    resultLost: (b, t) => `Plus d'essais — meilleur ${b}/${t}.`,
    shareResult: 'Partager le résultat',
    copied: 'Copié dans le presse-papiers',
    nextPuzzle: 'Prochain puzzle dans',
    themeToLight: 'Passer au thème clair',
    themeToDark: 'Passer au thème sombre',
    langLabel: 'Langue',
    disclaimer: 'Un puzzle quotidien indépendant · dates issues de sources publiques',
    inspiredBy: 'Inspiré de hisorty.app',
    play: 'Jouer',
    endless: 'Infini',
    nextRound: 'Suivant',
    shareImage: "Partager l'image",
    statStreak: 'Série',
    statBest: 'Record',
    statPlayed: 'Parties',
    statWin: '% gagné',
    solvedLabel: 'Résolus',
  },
}
